
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/context/AuthContext";
import { useOnboarding } from "../OnboardingProvider";
import { useOnboardingProgress } from "@/hooks/useOnboardingProgress";

interface TermsStepProps {
  onNext: () => void;
  onPrev: () => void;
}

export function TermsStep({ onNext, onPrev }: TermsStepProps) {
  const { user } = useAuth();
  const { completeStep } = useOnboarding();
  const { updateProgress } = useOnboardingProgress();
  const { toast } = useToast();
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleAcceptTerms = async () => {
    if (!termsAccepted || !user) return;

    setIsSubmitting(true);

    try {
      // Salvar aceitação dos termos
      const { error } = await supabase
        .from('user_terms_acceptance' as any)
        .insert({
          user_id: user.id,
          terms_version: '1.0',
          ip_address: 'unknown', // Em produção, você pode capturar o IP real
        });

      if (error) throw error;

      // Marcar step como completo e atualizar progresso
      completeStep('terms');
      await updateProgress({ terms_accepted: true });

      toast({
        title: "Termos aceitos!",
        description: "Obrigado por aceitar nossos termos de uso.",
      });

      onNext();
    } catch (error: any) {
      console.error('Erro ao aceitar termos:', error);
      toast({
        title: "Erro",
        description: "Não foi possível registrar a aceitação dos termos",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-xl font-semibold mb-2">Termos de Uso</h2>
        <p className="text-muted-foreground">
          Para continuar, é necessário aceitar nossos termos de uso.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Termos e Condições</CardTitle>
          <CardDescription>
            Leia e aceite nossos termos para finalizar o cadastro
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="bg-muted p-4 rounded-lg max-h-40 overflow-y-auto">
            <div className="text-sm space-y-2">
              <p><strong>1. Uso da Plataforma</strong></p>
              <p>Ao utilizar nossa plataforma de cashback, você concorda em seguir todas as diretrizes e políticas estabelecidas.</p>
              
              <p><strong>2. Programa de Cashback</strong></p>
              <p>O cashback é calculado com base nas regras configuradas por você e aplicado conforme os termos estabelecidos.</p>
              
              <p><strong>3. Dados e Privacidade</strong></p>
              <p>Seus dados são tratados conforme nossa política de privacidade, garantindo segurança e confidencialidade.</p>
              
              <p><strong>4. Responsabilidades</strong></p>
              <p>Você é responsável pela configuração adequada das regras de cashback e pelo uso correto da plataforma.</p>
            </div>
          </div>

          <div className="flex items-center space-x-2">
            <Checkbox
              id="terms"
              checked={termsAccepted}
              onCheckedChange={(checked) => setTermsAccepted(checked as boolean)}
            />
            <label
              htmlFor="terms"
              className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
            >
              Eu li e aceito os termos e condições de uso
            </label>
          </div>
        </CardContent>
      </Card>

      <div className="flex gap-3">
        <Button variant="outline" onClick={onPrev} className="flex-1">
          Voltar
        </Button>
        <Button 
          onClick={handleAcceptTerms}
          disabled={!termsAccepted || isSubmitting}
          className="flex-1"
        >
          {isSubmitting ? "Processando..." : "Aceitar e Continuar"}
        </Button>
      </div>
    </div>
  );
}
